/**
 * Wave 94 — live presence pill for the top bar.
 *
 * Two responsibilities:
 *   1. Heartbeat — every 30 s POST /v1/me/heartbeat with the current
 *      pathname so the server knows who's where. Runs as long as the
 *      tab is visible; pauses on hidden tabs to keep noise down.
 *   2. Avatar stack — admins/PMs see the last few online users with
 *      a tooltip showing their last route and how long ago they
 *      moved. Click the stack → expands into a popover with the full
 *      list.
 *
 * Designed to be quiet: 12 px avatars stacked with -8 px overlap, no
 * names by default, hover/tap to reveal. Mobile shows a single dot
 * count instead of the avatars.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/api/client';
import { useAuth } from '@/store/auth.store';
import { Avatar } from '@/components/ui/Avatar';
import { useT } from '@/hooks/useT';

interface PresenceRow {
  user_id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;
  last_seen_at: string;
  last_route: string | null;
  client_kind: string;
}

export function PresencePill() {
  const { locale } = useT();
  const isEn = locale === 'en';
  const user = useAuth((s) => s.user);
  const token = useAuth((s) => s.token);
  const location = useLocation();
  const [rows, setRows] = useState<PresenceRow[]>([]);
  const [open, setOpen] = useState(false);

  const canSeePresence = user?.role === 'admin' || user?.role === 'project_manager';

  // Heartbeat: pulse every 30s with current pathname. Pause when tab is
  // hidden — there's no value in claiming "online" while the screen is
  // locked. Resumes the moment the tab comes back.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const beat = () => {
      if (cancelled || document.hidden) return;
      api.heartbeat(location.pathname).catch(() => { /* ignore */ });
    };
    beat();
    const iv = window.setInterval(beat, 30_000);
    const onVisible = () => { if (!document.hidden) beat(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [token, location.pathname]);

  // Pull the online list every 45s when the user can see it.
  useEffect(() => {
    if (!canSeePresence || !token) return;
    let cancelled = false;
    const pull = async () => {
      try {
        const r = await api.listPresence();
        if (!cancelled) setRows(r);
      } catch {
        if (!cancelled) setRows([]);
      }
    };
    pull();
    const iv = window.setInterval(pull, 45_000);
    return () => { cancelled = true; window.clearInterval(iv); };
  }, [canSeePresence, token]);

  if (!canSeePresence || rows.length === 0) return null;

  // Up to 4 avatars in the stack; the rest collapse into a "+N" chip.
  const stack = rows.slice(0, 4);
  const overflow = Math.max(0, rows.length - stack.length);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="focus-ring group flex min-h-[44px] items-center gap-1 rounded-full border border-line bg-surface-2/50 py-0.5 pl-1 pr-2 transition-colors hover:bg-surface-2"
        title={isEn ? `${rows.length} people online` : `${rows.length} pessoas online`}
        aria-label={isEn ? 'People online' : 'Pessoas online'}
      >
        <div className="flex">
          {stack.map((r, i) => (
            <div
              key={r.user_id}
              className="relative -ml-1.5 first:ml-0"
              style={{ zIndex: stack.length - i }}
            >
              <Avatar name={r.full_name} src={r.avatar_url} size="sm" />
              <span
                aria-hidden
                className="absolute bottom-0 right-0 h-1.5 w-1.5 rounded-full bg-emerald-400 ring-2 ring-surface"
              />
            </div>
          ))}
        </div>
        <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-ink-muted group-hover:text-ink">
          {rows.length}
          {overflow > 0 && `+`}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-xl border border-line bg-surface shadow-elev-3 p-2">
          <p className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-ink-subtle">
            {isEn ? 'Online now' : 'Online agora'}
          </p>
          <ul className="space-y-1">
            {rows.map((r) => (
              <li key={r.user_id} className="flex min-h-[44px] items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-surface-2">
                <div className="relative">
                  <Avatar name={r.full_name} src={r.avatar_url} size="sm" />
                  <span
                    aria-hidden
                    className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-emerald-400 ring-2 ring-surface"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-ink">{r.full_name}</p>
                  <p className="truncate text-[11px] text-ink-subtle">
                    {humanizeRoute(r.last_route, isEn)} · {timeAgo(r.last_seen_at, isEn)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function humanizeRoute(route: string | null, isEn: boolean): string {
  if (!route) return 'app';
  if (route === '/' || route === '/projects') return isEn ? 'projects' : 'projetos';
  if (route.startsWith('/projects/')) {
    const seg = route.split('/').slice(2);
    if (seg.length === 1) return isEn ? 'project' : 'projeto';
    return seg[seg.length - 1] ?? (isEn ? 'project' : 'projeto');
  }
  if (route.startsWith('/anomalias')) return isEn ? 'issues' : 'anomalias';
  if (route.startsWith('/aprovacoes')) return isEn ? 'approvals' : 'aprovações';
  if (route.startsWith('/atividade')) return isEn ? 'activity' : 'atividade';
  if (route.startsWith('/carteira')) return isEn ? 'portfolio' : 'carteira';
  if (route.startsWith('/settings')) return isEn ? 'settings' : 'ajustes';
  return route.replace(/^\//, '');
}

function timeAgo(iso: string, isEn: boolean): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 30) return isEn ? 'now' : 'agora';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h`;
}
